import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { darken, lighten } from 'polished';
import { useTheme } from '@material-ui/core/styles';
import MaterialButton from '@material-ui/core/Button';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Icon from '../Icon';
import {
    rem,
    setSizes,
    resolveColor,
    applyStyle
} from '../../../helpers/styles_mixins';
import {
    applyBreakpoints,
    fontWeight as fontWeightFunc
} from '../../../config/styles/mixins';

const StyledButton = styled(MaterialButton)`
  position: relative !important;

  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')} !important;
  justify-content: center !important;
  align-content: center !important;
  align-items: center !important;

  margin: ${({ margin }) => setSizes(margin)} !important;
  padding: ${({ padding }) => setSizes(padding)} !important;
  width: ${({ width }) => width} !important;
  min-width: ${({ minwidth }) => minwidth} !important;
  max-width: ${({ maxwidth }) => maxwidth} !important;
  height: ${({ height }) => height} !important;
  min-height: ${({ minheight }) => minheight} !important;
  max-height: ${({ maxheight }) => maxheight} !important;

  border: ${({ border }) => border}  !important;
  border-radius: ${({ radius }) => setSizes(radius)} !important;

  font-family: ${({ fontFamily }) => fontFamily} !important;
  font-weight: ${({ fontWeight }) => fontWeightFunc(fontWeight)} !important;
  font-size: ${({ fontSize }) => rem(fontSize)} !important;
  text-transform: ${({ texttransform }) => texttransform} !important;
  text-decoration: none !important;

  transition: all 0.2s ease-in-out !important;
  cursor: pointer !important;
  box-sizing: border-box !important;
  overflow: none !important;
  outline: 0 !important;
  float: ${({ float }) => float};

  ${({
      theme,
      customcolor,
      inverted,
      fontcolor,
      gradient,
      radius,
      bgcolor,
      bordersize,
      bordercolor
  }) =>
      inverted
          ? css`
                background-color: transparent !important;
                color: ${resolveColor(customcolor, theme)} !important;

                &:after {
                    content: '' !important;
                    display: block !important;
                    box-sizing: border-box !important;
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;

                    width: 100% !important;
                    height: 100% !important;
                    background-color: transparent !important;
                    border-radius: ${setSizes(radius)} !important;

                    transition: all 0.2s ease-in-out !important;

                    border: bordersize solid ${resolveColor(bordercolor, theme)} !important;

                    ${gradient &&
                        css`
                            border-image: linear-gradient(
                                ${gradient(
                                    'to right',
                                    theme.gradients[customcolor]
                                )}
                            ) !important;
                            border-image-slice: 1 !important;
                        `}
                }

                &:hover {
                    color: #c3cd065e !important;

                    &:after {
                        border-width: 3px !important;

                        border-color: ${lighten(
                            0.1,
                            resolveColor(customcolor, theme)
                        )} !important;
                    }
                }

                &:active {
                    color: ${darken(
                        0.1,
                        resolveColor(customcolor, theme)
                    )} !important;

                    &:after {
                        border-color: ${darken(
                            0.1,
                            resolveColor(customcolor, theme)
                        )} !important;
                    }
                }
            `
          : css`
                border: 1px solid ${resolveColor(bordercolor, theme)} !important;
                color: ${resolveColor(fontcolor, theme)} !important;

                ${gradient
                    ? css`
                          background: linear-gradient(
                              ${gradient('348.69deg', {
                                  0: '#F1FF00',
                                  35: '#F1FF00',
                                  100: '#757573'
                              })}
                          ) !important;
                          background-size: 200% 100% !important;
                          transition: background-position 0.3s ease-in-out !important;

                          &:hover {
                              background-position-x: 50% !important;
                          }

                          &:active {
                              background-position-x: 100% !important;
                          }
                      `
                    : css`
                          background-color: ${resolveColor(
                              bgcolor,
                              theme
                          )} !important;

                          border-color: ${resolveColor(
                              bordercolor,
                              theme
                          )} !important;

                          &:hover {
                              background-color: #c3cd065e !important;

                              border-color: #c2cd06 !important;
                          }

                          &:active {
                              background-color: ${darken(
                                  0.1,
                                  resolveColor(bgcolor, theme)
                              )} !important;

                              border-color: #c2cd06 !important;
                          }
                      `}
            `}

  ${({ disabled, gradient }) =>
      disabled &&
      css`
          opacity: 0.8 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
          ${gradient &&
              css`
                  background-color: #dbdbdb !important;
                  background-image: none !important;
              `}
      `}

  ${applyStyle}
  ${applyBreakpoints}
`;

const Button = ({
    fontSize,
    icon,
    iconPosition,
    onClick,
    customClick,
    customClickParams,
    children,
    width,
    ...props
}) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));

    let localWidth = width;
    if (!matches) {
        localWidth = undefined;
    }
    return (
        <StyledButton
            onClick={ev => {
                customClick ? customClick(customClickParams) : onClick(ev);
            }}
            fontSize={fontSize}
            width={localWidth}
            {...props}
        >
            {icon && iconPosition === 'before' && (
                <Icon
                    type={icon}
                    margin={[0, 8, 0, 0]}
                    size={fontSize * 1.5}
                    fillColor='currentColor'
                />
            )}
            {children}
            {icon && iconPosition === 'after' && (
                <Icon
                    type={icon}
                    margin={[0, 0, 0, 8]}
                    size={fontSize * 1.5}
                    fillColor='currentColor'
                />
            )}
        </StyledButton>
    );
};

Button.propTypes = {
    children: PropTypes.node,
    onClick: PropTypes.func,
    customClick: PropTypes.func,
    disabled: PropTypes.bool,
    customcolor: PropTypes.string,
    fontFamily: PropTypes.string,
    fontWeight: PropTypes.oneOf(['Light', 'Regular', 'Medium', 'Bold']),
    fontSize: PropTypes.number,
    texttransform: PropTypes.string,
    bgcolor: PropTypes.string,
    border: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            top: PropTypes.string,
            bottom: PropTypes.string,
            left: PropTypes.string,
            right: PropTypes.string
        })
    ]),
    margin: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    radius: PropTypes.arrayOf(PropTypes.number),
    width: PropTypes.string,
    height: PropTypes.string,
    minwidth: PropTypes.string,
    minheight: PropTypes.string,
    customclick: PropTypes.func,
    customClickParams: PropTypes.shape({}),
    icon: PropTypes.string,
    iconPosition: PropTypes.oneOf(['before', 'after']),
    maxwidth: PropTypes.string,
    maxheight: PropTypes.string,
    fontcolor: PropTypes.string,
    float: PropTypes.string,
    bordercolor: PropTypes.string,
    bordersize: PropTypes.string
};

Button.defaultProps = {
    children: undefined,
    onClick: undefined,
    customClick: undefined,
    width: '375px',
    height: 'initial',
    minwidth: 'initial',
    minheight: 'initial',
    maxwidth: 'initial',
    maxheight: 'initial',
    margin: [5, 10],
    padding: [15, 50],
    radius: [4],
    customcolor: 'colorButtonActive',
    fontFamily: 'Simplon, OiTextRegular, simplon_bpregular, sans-serif',
    fontSize: 16,
    fontWeight: 'Regular',
    fontcolor: 'dark',
    disabled: false,
    customClickParams: {},
    iconPosition: 'before',
    texttransform: 'none',
    bgcolor: 'colorButtonActive',
    border: '0',
    customclick: undefined,
    icon: undefined,
    float: undefined,
    bordercolor: 'white',
    bordersize: '0px'
};

export { Button };
export default Button;
